import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
    LayoutDashboard,
    Package,
    Tags,
    Award,
    ShoppingCart,
    Users,
    Truck,
    PackageCheck,
    Settings as SettingsIcon,
    LogOut,
    ExternalLink,
    Instagram as InstagramIcon,
    Ticket,
    Wrench,
    Menu,
    X,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/categories", label: "Categories", icon: Tags },
    { to: "/admin/brands", label: "Brands", icon: Award },
    { to: "/admin/customers", label: "Customers", icon: Users },
    { to: "/admin/discounts", label: "Discounts", icon: Ticket },
    { to: "/admin/instagram", label: "Instagram", icon: InstagramIcon },
    { to: "/admin/shipping", label: "Shipping", icon: Truck },
    { to: "/admin/catalog/shipping-audit", label: "Catalog Shipping Audit", icon: PackageCheck },
    { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
    { to: "/admin/maintenance", label: "Maintenance", icon: Wrench },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const { settings } = useSettings();
    const nav = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const onLogout = async () => {
        await logout();
        nav("/admin/login");
    };

    return (
        <div className="min-h-screen bg-[#0F0F13] text-white flex">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:flex w-64 bg-[#09090B] border-r border-[#27272A] flex-col fixed inset-y-0">
                <div className="p-5 border-b border-[#27272A]">
                    <Link to="/admin" className="flex items-center gap-3" data-testid="admin-sidebar-logo">
                        <img src={settings?.logo_url} alt="Opensea" className="h-12 w-auto max-h-12 object-contain" />
                    </Link>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-display mt-2">Admin Console</div>
                </div>
                <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3.5 py-2.5 rounded-sm text-sm font-display uppercase tracking-widest font-semibold transition-colors ${
                                    isActive
                                        ? "bg-[#00BCE5]/10 text-[#00BCE5] border-l-2 border-[#00BCE5] pl-3"
                                        : "text-white/70 hover:bg-[#121214] hover:text-white"
                                }`
                            }
                            data-testid={`admin-nav-${n.label.toLowerCase()}`}
                        >
                            <n.icon size={16} />
                            {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-[#27272A]">
                    <a
                        href="/"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-xs text-white/55 hover:text-[#00BCE5] uppercase tracking-widest font-display py-2"
                        data-testid="admin-view-store"
                    >
                        <ExternalLink size={14} /> View Store
                    </a>
                    <div className="text-[11px] text-white/45 mt-1 truncate">{user?.email}</div>
                    <button
                        onClick={onLogout}
                        className="mt-2 w-full flex items-center gap-2 px-3 py-2 border border-[#27272A] rounded-sm text-sm text-white/85 hover:border-[#FF3B30] hover:text-[#FF3B30] uppercase tracking-widest font-display"
                        data-testid="admin-logout"
                    >
                        <LogOut size={14} /> Logout
                    </button>
                </div>
            </aside>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="lg:hidden fixed inset-0 z-50">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
                    <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#09090B] border-r border-[#27272A] flex flex-col">
                        <div className="p-5 border-b border-[#27272A] flex items-center justify-between">
                            <img src={settings?.logo_url} alt="Opensea" className="h-11 w-auto max-h-11 object-contain" />
                            <button onClick={() => setMobileOpen(false)} className="text-white p-1">
                                <X size={20} />
                            </button>
                        </div>
                        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.end}
                                    onClick={() => setMobileOpen(false)}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-3.5 py-2.5 rounded-sm text-sm font-display uppercase tracking-widest font-semibold ${
                                            isActive ? "bg-[#00BCE5]/10 text-[#00BCE5]" : "text-white/70"
                                        }`
                                    }
                                >
                                    <n.icon size={16} />
                                    {n.label}
                                </NavLink>
                            ))}
                        </nav>
                        <button
                            onClick={onLogout}
                            className="m-3 flex items-center gap-2 justify-center px-3 py-2 border border-[#27272A] rounded-sm text-sm uppercase tracking-widest font-display"
                        >
                            <LogOut size={14} /> Logout
                        </button>
                    </aside>
                </div>
            )}

            {/* Main */}
            <div className="flex-1 lg:ml-64 min-h-screen flex flex-col">
                <header className="sticky top-0 z-30 bg-[#0F0F13]/95 backdrop-blur border-b border-[#27272A] px-4 md:px-6 py-3 flex items-center gap-3">
                    <button
                        onClick={() => setMobileOpen(true)}
                        className="lg:hidden p-2 -ml-1 text-white"
                        aria-label="Menu"
                        data-testid="admin-mobile-menu"
                    >
                        <Menu size={20} />
                    </button>
                    <div className="font-display uppercase tracking-widest text-sm text-white/65">
                        {settings?.store_name || "Opensea Fishing Tackles"} · Admin
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                        <a
                            href="/"
                            target="_blank"
                            rel="noreferrer"
                            className="hidden md:inline-flex items-center gap-2 text-xs text-white/65 hover:text-[#00BCE5] uppercase tracking-widest font-display"
                        >
                            <ExternalLink size={13} /> View store
                        </a>
                    </div>
                </header>
                <main className="flex-1 p-4 md:p-6 lg:p-8 animate-fade-up">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
